import type { LucideIcon } from "lucide-react";
import * as React from "react";
import { cn } from "../lib/utils.js";

/**
 * EmptyState (W-3): a title, an optional line of detail and exactly one
 * action (the caller's link or button). Copy arrives translated.
 */

export interface EmptyStateProps extends Omit<React.ComponentProps<"section">, "title"> {
  title: string;
  body?: string;
  icon?: LucideIcon;
  /** The one action. */
  action: React.ReactNode;
  headingLevel?: 2 | 3;
  /** Makes the heading a programmatic focus target (e.g. after the list it replaces is gone). */
  headingRef?: React.Ref<HTMLHeadingElement>;
}

function EmptyState({ title, body, icon: Icon, action, headingLevel = 2, headingRef, className, ...props }: EmptyStateProps) {
  const id = React.useId();
  const Heading = `h${headingLevel}` as "h2";
  return (
    <section
      data-slot="empty-state"
      aria-labelledby={`${id}-title`}
      className={cn(
        "flex flex-col items-center gap-3 rounded-lg border border-dashed bg-card p-6 text-center text-card-foreground",
        className,
      )}
      {...props}
    >
      {Icon ? <Icon aria-hidden="true" className="size-8 text-primary" /> : null}
      <Heading
        id={`${id}-title`}
        ref={headingRef}
        tabIndex={headingRef ? -1 : undefined}
        className="text-lg font-semibold"
      >
        {title}
      </Heading>
      {body ? <p className="text-sm text-muted-foreground">{body}</p> : null}
      <div>{action}</div>
    </section>
  );
}

export { EmptyState };
